import {
  ArrowsRightLeftIcon,
  BeakerIcon,
  ArchiveBoxIcon,
  BellAlertIcon,
  BoltIcon,
  CircleStackIcon,
  LockClosedIcon,
  SignalSlashIcon,
  ExclamationCircleIcon,
  LinkIcon,
  NoSymbolIcon,
  PencilIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  TagIcon,
} from "@heroicons/react/20/solid";
import type { ComponentType, SVGProps } from "react";
import { OtsTooltip } from "../components/OtsTooltip";
import { activityMarkerToneClasses } from "./driver-activity-theme";
import type { ActivityMarker } from "./driver-activity-view-model";

const icons: Record<ActivityMarker["id"], ComponentType<SVGProps<SVGSVGElement>>> = {
  alert: BoltIcon,
  lact: CircleStackIcon,
  "scada-off": SignalSlashIcon,
  "qty-locked": LockClosedIcon,
  holding: ArchiveBoxIcon,
  split: ArrowsRightLeftIcon,
  "well-stage": BeakerIcon,
  restriction: BellAlertIcon,
  manual: PencilIcon,
  disputed: ExclamationCircleIcon,
  rejected: NoSymbolIcon,
  "dispatch-link": LinkIcon,
  compliance: ShieldCheckIcon,
  coding: TagIcon,
};

export type ActivityMarkerIconsProps = {
  markers: ActivityMarker[];
  /** Most icons shown; past it the least urgent fold into a "+N" chip that lists them. */
  max?: number;
  className?: string;
};

const toneRank: Partial<Record<ActivityMarker["tone"], number>> = { danger: 0, warning: 1 };

/** Keeps the most urgent markers in view, in their original order, when not all of them fit. */
const splitMarkers = (markers: ActivityMarker[], max: number): [ActivityMarker[], ActivityMarker[]] => {
  if (markers.length <= max) return [markers, []];
  // One slot goes to the "+N" chip.
  const kept = new Set(markers
    .map((marker, index) => ({ marker, index }))
    .sort((left, right) => (toneRank[left.marker.tone] ?? 2) - (toneRank[right.marker.tone] ?? 2) || left.index - right.index)
    .slice(0, Math.max(1, max - 1))
    .map(({ marker }) => marker));
  return [markers.filter((marker) => kept.has(marker)), markers.filter((marker) => !kept.has(marker))];
};

/** The small colored icons that carry a ticket's side facts: geofence, split, restriction, dispute, compliance… */
export const ActivityMarkerIcons = ({ markers, max = Infinity, className }: ActivityMarkerIconsProps) => {
  if (!markers.length) return null;
  const [shown, hidden] = splitMarkers(markers, max);

  return (
    <span className={["inline-flex shrink-0 items-center gap-0.5", className].filter(Boolean).join(" ")}>
      {shown.map((marker) => {
        const Icon = marker.id === "compliance" && marker.tone === "danger" ? ShieldExclamationIcon : icons[marker.id];
        return (
          <OtsTooltip key={marker.id} content={marker.label} density="compact">
            <span tabIndex={0} role="img" aria-label={marker.label} data-marker={marker.id} className={["relative inline-flex", activityMarkerToneClasses[marker.tone]].join(" ")}>
              <Icon aria-hidden="true" className="size-3.5"/>
              {marker.count != null && marker.count > 1 && <span aria-hidden="true" className="ml-px text-[9px] font-bold leading-none">{marker.count}</span>}
            </span>
          </OtsTooltip>
        );
      })}
      {hidden.length > 0 && (
        <OtsTooltip
          density="compact"
          content={<ul className="space-y-0.5">{hidden.map((marker) => <li key={marker.id}>{marker.label}</li>)}</ul>}>
          <span
            tabIndex={0}
            role="img"
            aria-label={`${hidden.length} more: ${hidden.map((marker) => marker.label).join(", ")}`}
            className="rounded bg-[var(--ots-neutral-container)] px-1 text-[9px] font-bold leading-[14px] text-[var(--ots-on-neutral-container)]">
            +{hidden.length}
          </span>
        </OtsTooltip>
      )}
    </span>
  );
};
